import { createSelector } from 'reselect';
import { IAppStore } from '../../reducer';
import { CallConnectionState, IGroupParticipant } from './reducer';

const getCallConnectionState = (state: IAppStore) =>
  state.groupCalling.connectionState;

export const getCallConnectionStateSelector = createSelector(
  [getCallConnectionState],
  (connectionState) => connectionState);

const getCallAccessToken = (state: IAppStore) => state.groupCalling.accessToken;

export const getCallAccessTokenSelector = createSelector(
  [getCallAccessToken],
  (acessToken) => acessToken);

const getCallParticipants = (state: IAppStore) => state.groupCalling.participants;
const getCurrentSequence = (state: IAppStore) => state.homeWorkouts.currentSessionSequenceInfo &&
  state.homeWorkouts.currentSessionSequenceInfo.currentSequence;
const getLargeParticipantViewIsOpen = (state: IAppStore) => state.homeWorkouts.largeParticipantViewIsOpen;
export interface INumberOfRows {
  vertical: number;
  horizontal: number;
}

const getNumberOfRows = (
  numberOfParticipants: number,
  largeParticipantViewIsOpen: boolean): INumberOfRows => {
  if (numberOfParticipants <= 2) {
    return {
      vertical: 1,
      horizontal: 2,
    };
  }

  if (!largeParticipantViewIsOpen) {
    return {
      vertical: 2,
      horizontal: 2,
    };
  }

  if (numberOfParticipants <= 3 ) {
    return {
      vertical: 1,
      horizontal: 3,
    };
  }

  if (numberOfParticipants <= 4) {
    return {
      vertical: 2,
      horizontal: 3,
    };
  }

  return {
    vertical: 2,
    horizontal: 3,
  };
};

interface ISortableParticipants extends IGroupParticipant {
  sortKey: number;
}

export const getCallParticipantsAsArraySelector = createSelector(
  [
    getCallParticipants,
    getCurrentSequence,
    getLargeParticipantViewIsOpen,
    (state: IAppStore) => state.homeWorkouts.userProfile?.uid,
  ],
  (participantMap, currentSequence, largeParticipantViewIsOpen, myUserProfileId): {
    participants: IGroupParticipant[][],
    numberOfRows: INumberOfRows } => {
    const defaultResponse = {
      participants: [],
      numberOfRows: {
        vertical: 0,
        horizontal: 0,
      },
    };
    try {
      if (!currentSequence) {
        return defaultResponse;
      }
      const allParticipants: ISortableParticipants[] = Object.keys(participantMap)
      .map((userId) => {
        const groupParticipant = participantMap[userId] as IGroupParticipant;
        return {
          ...groupParticipant,
          sortKey: groupParticipant.userProfileId === myUserProfileId ? -1 :
            groupParticipant.agoraUid && groupParticipant.videoEnabled ? 0 : 1,
        };
      });

      const participants = allParticipants.sort(
        (participantA: ISortableParticipants, participantB: ISortableParticipants) =>
          participantA.sortKey - participantB.sortKey);

      const numberOfRows = getNumberOfRows(participants.length, largeParticipantViewIsOpen);
      const result: IGroupParticipant[][] = [];
      for (let participantIndex = 0; participantIndex <
        participants.length; participantIndex += numberOfRows.horizontal) {
        const resultForCurrentSet: IGroupParticipant[] = [];
        for (let subGroupIndex = 0; subGroupIndex < numberOfRows.horizontal; subGroupIndex++) {
          if (participants[participantIndex + subGroupIndex]) {
            resultForCurrentSet.push(participants[participantIndex + subGroupIndex]);
          }
        }
        result.push(resultForCurrentSet);
      }
      return {
        participants: result,
        numberOfRows,
      };
    } catch (e) {
      return defaultResponse;
    }
  });

const getCallShouldDisconnect = (state: IAppStore) => state.groupCalling.shouldDisconnect;

export const getCallShouldDisconnectSelector = createSelector(
  [getCallShouldDisconnect],
  (shouldDisconnect): boolean => shouldDisconnect);

const getIsGroupWorkoutCallConnected = (state: IAppStore) =>
  state.groupCalling.connectionState === CallConnectionState.connected;
const getIsGroupWorkoutCallConnecting = (state: IAppStore) =>
  state.groupCalling.connectionState === CallConnectionState.connecting;
const getIsGroupWorkoutCallDisconnected = (state: IAppStore) =>
  state.groupCalling.connectionState === CallConnectionState.disconnected;

export const isGroupWorkoutCallConnectedSelector = createSelector(
  [getIsGroupWorkoutCallConnected],
  (isGroupWorkoutCallConnected) => isGroupWorkoutCallConnected,
);

export const isGroupWorkoutCallConnectingSelector = createSelector(
  [getIsGroupWorkoutCallConnecting],
  (isGroupWorkoutCallConnecting) => isGroupWorkoutCallConnecting,
);

export const isGroupWorkoutCallDisconnectedSelector = createSelector(
  [getIsGroupWorkoutCallDisconnected],
  (isGroupWorkoutCallDisconnected) => isGroupWorkoutCallDisconnected,
);

export const selectUseAllAvailableSpace = createSelector(
  [getCallParticipants],
  (participantMap) => Object.keys(participantMap).length === 1,
);
